import { useCallback, useEffect, useState } from "react"
import { retryDecorator } from "ts-retry-promise"
import { finishWebView } from "../shared/app-interface"
import { JOB_DETAILS } from "../shared/constants"
import { QUESTION_TYPE } from "../shared/constants/assessment-flow"
import { EVAL_STATUS_MAPPING } from "../shared/constants/eval-status-mapping"
import { ASSESSMENTS_EVENTS, EVENTS } from "../shared/constants/MIXAPANEL_EVENTS"
import {
    APPLIED_JOBS,
    EVAL_ASSESSMENT,
    GET_ASSESSMENT,
    GET_JOB_DETAILS,
    GET_SCREEN_DETAILS,
} from "../shared/constants/URLS"
import { httpClient } from "../shared/http-client"
import { useAssessmentStore } from "../shared/store"
import { CompletionObjectType } from "../shared/utils/assessment-types"
import { getSectionName, trackMixpanelEvent } from "../utils/helpers"
import Assessment from "./assessment"
import AssessmentFailedSheet from "./assessment-failed-sheet"
import AssessmentSuccess from "./assessment-success"
import AudioAssessment from "./audio-assessment/audio-assessment"
import FullScreenLoader from "./audio-assessment/loader/full-screen-loader"
import JobPreferenceAssessment from "./job-preference/job-preference"
import ResumeAssessment from "./resume-assessment/resume-assessment"
import AssetAssessment from "./asset-assessment/asset-assessment"

const Flow = () => {
    const {
        currentStep,
        assesmentQuestions,
        answeredQuestions,
        openSuccessFlow,
        evalAssessment,
        setJobDetails,
        setAssessmentQuestions,
        setScreenDetails,
        setEvalAssessment,
        setOpenSuccessFlow,
        onSkipStep,
        setAnsweredQuestions,
        updateCurrentStep,
    } = useAssessmentStore((state) => state)

    const queryString = window.location.search
    const urlParams = new URLSearchParams(queryString)
    const jobId = urlParams.get("job_id") as string
    const webSuccessParam = urlParams.get("web_success_flow") || "true" // flag for displaying web success or android
    const webSuccessFlow = webSuccessParam === "true"
    sessionStorage.setItem("web_success_flow", webSuccessFlow as unknown as string)
    const [isLoadingJobDetails, setIsLoadingJobDetails] = useState(true)
    const [isLoadingAssessment, setIsLoadingAssessment] = useState(true)
    const [loadingSuccess, setloadingSuccess] = useState(false)

    const questionId = assesmentQuestions?.questions[currentStep]?.questions[0]?.id
    const questionType = assesmentQuestions?.questions[currentStep]?.question_type
    const totalQuestions = assesmentQuestions?.questions.length
    const isLastStep = currentStep === totalQuestions - 1
    const isVasFlow = assesmentQuestions?.mandatory
    const assessmentStatus = evalAssessment?.assessment_results?.assessment_status
    const openFailedSheet = assessmentStatus === "FAILED"
    const showloader = isLoadingJobDetails || isLoadingAssessment
    const entity = assesmentQuestions?.questions[currentStep]?.entity_type

    const exitWebSuccess = () => {
        const data = {
            intent: "navigate",
            flowType: isVasFlow ? "vas" : "enrichment",
            navigateTo: "screen_details",
        }
        finishWebView(JSON.stringify(data))
    }
    const fetchSuccess = useCallback(async () => {
        setloadingSuccess(true)
        try {
            await httpClient
                .get(GET_SCREEN_DETAILS.replace("{jobId}", jobId))
                .then((response) => {
                    setScreenDetails(response?.data?.response)
                    setloadingSuccess(false)
                })
        } catch (err) {
            console.log(err)
            setloadingSuccess(false)
        }
    }, [jobId, setScreenDetails])
    const createLead = async (status: any) => {
        const appliedJobData = {
            category_auto_update: true,
            job: jobId,
            remark: "",
            status: (EVAL_STATUS_MAPPING as any)[status as any],
        }
        setloadingSuccess(true)
        try {
            await httpClient.post(APPLIED_JOBS, appliedJobData).then(() => {
                setloadingSuccess(false)
                if (webSuccessFlow) {
                    setOpenSuccessFlow(true)
                } else {
                    exitWebSuccess()
                }
            })
        } catch (err) {
            console.log(err)
            setloadingSuccess(false)
        }
    }
    const evaluateAssessment = async (answers: any) => {
        const data = {
            answers: answers,
        }
        try {
            setloadingSuccess(true)
            const resp = await httpClient
                .post(EVAL_ASSESSMENT.replace("{jobId}", jobId), data)
                .then((response) => {
                    const status = response?.data?.assessment_results?.assessment_status
                    setEvalAssessment(response?.data)
                    setloadingSuccess(false)
                    if (webSuccessFlow) fetchSuccess()
                    trackMixpanelEvent(ASSESSMENTS_EVENTS.ASSESSMENT_RESULT, {
                        Result: status,
                        ...(status === "FAILED" && {
                            "Stage of Failure": "Round 2",
                        }),
                    })
                    return response
                })
            if (isVasFlow && resp?.status == 200) {
                const status = resp?.data?.assessment_results?.assessment_status
                createLead(status)
            } else if (!webSuccessFlow && !isVasFlow) {
                exitWebSuccess()
            }
        } catch (err) {
            console.log(err)
        } finally {
            // check vas flow
            if (!isVasFlow && webSuccessFlow) setOpenSuccessFlow(true)
        }
    }
    const handleOnCompletion = (completionObject: CompletionObjectType) => {
        if (!isLastStep) {
            setAnsweredQuestions(questionId, questionType, completionObject.answer_id)
            updateCurrentStep()
        } else {
            setAnsweredQuestions(questionId, questionType, completionObject?.answer_id)
            const answers = Array.from(answeredQuestions)
            answers.push(completionObject)
            evaluateAssessment(answers)
        }
    }
    const handleSkip = () => {
        trackMixpanelEvent(EVENTS.ASSESSMENT_ANSWERED, {
            "Section Name": getSectionName(entity),
        })
        if (isLastStep) {
            evaluateAssessment(Array.from(answeredQuestions))
            if (webSuccessFlow) setOpenSuccessFlow(true)
        } else onSkipStep()
    }
    useEffect(() => {
        const fetchDetails = async () => {
            await httpClient
                .get(GET_JOB_DETAILS.replace("{jobId}", jobId))
                .then((response) => {
                    setJobDetails(response?.data)
                    sessionStorage.setItem(JOB_DETAILS, JSON.stringify(response?.data))
                    setIsLoadingJobDetails(false)
                })

            await httpClient
                .get(GET_ASSESSMENT.replace("{jobId}", jobId))
                .then((response) => {
                    const mandatory = response?.data?.mandatory
                    // TODO: from android wrong invocation even then web is not to be opened
                    if (response?.data?.questions.length === 0) exitWebSuccess()
                    setAssessmentQuestions(response?.data)
                    if (!mandatory && webSuccessFlow) fetchSuccess()
                    setIsLoadingAssessment(false)
                    trackMixpanelEvent(EVENTS.WEB_ASSESMENT_SHOWN, {
                        isVasPresent: response?.data?.mandatory,
                        type: response?.data?.mandatory
                            ? "VAS"
                            : "VAS / Application flow enrichment",
                        "DL Enrichment": response?.data?.questions?.find(
                            (q: any) => q?.question_type === "Upload"
                        )
                            ? "Yes"
                            : "No",
                        "Audio Enrichment": response?.data?.questions?.find(
                            (q: any) => q?.question_type === "AudioUpload"
                        )
                            ? "Yes"
                            : "No",
                        "Resume Enrichment": response?.data?.questions?.find(
                            (q: any) => q?.question_type === "Resume"
                        )
                            ? "Yes"
                            : "No",
                        "Preference Reconfirm": response?.data?.questions?.find(
                            (q: any) => q?.question_type === "JobPreferencesIntent"
                        )
                            ? "Yes"
                            : "No",
                        "Asset Enrichment": response?.data?.questions?.find(
                            (q: any) => q?.question_type === "Asset"
                        )
                            ? "Yes"
                            : "No",
                        "Pan Card Enrichment": "No",
                        "RC Enrichment": "No",
                    })
                })
        }
        const fetchWithAutoRetry = retryDecorator(fetchDetails, { retries: 3 })
        fetchWithAutoRetry()
    }, [fetchSuccess, jobId, setAssessmentQuestions, setJobDetails, webSuccessFlow])

    if (showloader || assesmentQuestions?.mandatory === undefined) {
        return <FullScreenLoader />
    }

    return (
        <div>
            <>
                {questionType === QUESTION_TYPE.ASSET && (
                    <AssetAssessment
                        handleSkip={handleSkip}
                        evaluateAssessment={evaluateAssessment}
                    />
                )}
                {questionType === QUESTION_TYPE.AUDIO_UPLOAD && (
                    <AudioAssessment
                        handleOnCompletion={handleOnCompletion}
                        handleSkip={handleSkip}
                    />
                )}
                {questionType === QUESTION_TYPE.UPLOAD && (
                    <Assessment
                        handleOnCompletion={handleOnCompletion}
                        handleSkip={handleSkip}
                        webSuccessFlow={webSuccessFlow}
                    />
                )}
                {questionType === QUESTION_TYPE.RESUME_UPLOAD && (
                    <ResumeAssessment
                        handleOnCompletion={handleOnCompletion}
                        handleSkip={handleSkip}
                        webSuccessFlow={webSuccessFlow}
                    />
                )}
                {questionType === QUESTION_TYPE.JOB_PREFERENCE && (
                    <JobPreferenceAssessment
                        handleOnCompletion={handleOnCompletion}
                        handleSkip={handleSkip}
                    />
                )}

                <AssessmentSuccess open={openSuccessFlow} />
                <AssessmentFailedSheet open={openFailedSheet} />
                {loadingSuccess && <FullScreenLoader />}
            </>
        </div>
    )
}
export default Flow
